/**
 * Contenido editorial del sitio público (marca personal, servicios, inversión, FAQ).
 * Centralizado acá para que el SSR y el JSON-LD puedan usarlo sin depender del contexto de React.
 */

import type { SeoLang } from "./seo";

export const WHATSAPP_NUMBER = "595986733480";

export function whatsappLink(lang: SeoLang) {
  const msg = {
    es: "Hola Dahiana, quisiera agendar una reunión para hablar de una propiedad.",
    en: "Hi Dahiana, I'd like to schedule a meeting to talk about a property.",
    pt: "Olá Dahiana, gostaria de agendar uma reunião para falar sobre um imóvel.",
  }[lang];
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
}

type Credential = { icon: "trophy" | "medal" | "handshake"; label: string };
type Service = { n: string; title: string; text: string; cta: string; to: string };
type Benefit = { title: string; text: string };
type Faq = { q: string; a: string };
type Milestone = { n: string; title: string; subtitle?: string; text: string };

export type SiteCopy = {
  brand: {
    eyebrow: string;
    name: string;
    tagline: string;
    ctaProperties: string;
    ctaMeeting: string;
    credentials: Credential[];
  };
  quote: { text: string; author: string };
  stats: {
    title: string;
    portfolio: string;
    deals: string;
    years: string;
    zones: string;
  };
  services: { eyebrow: string; title: string; items: Service[] };
  invest: { eyebrow: string; title: string; subtitle: string; cta: string; items: Benefit[] };
  faq: { eyebrow: string; title: string; subtitle: string; items: Faq[] };
  timeline: { eyebrow: string; title: string; items: Milestone[] };
  fab: string;
};

export const SITE_COPY: Record<SeoLang, SiteCopy> = {
  es: {
    brand: {
      eyebrow: "Asesora inmobiliaria · Asunción y Central",
      name: "Dahiana Marín",
      tagline: "Asesoría inmobiliaria estratégica para quienes piensan en años.",
      ctaProperties: "Ver propiedades",
      ctaMeeting: "Agendar reunión",
      credentials: [
        { icon: "trophy", label: "Cartera curada de propiedades premium" },
        { icon: "medal", label: "Atención personalizada de principio a fin" },
        { icon: "handshake", label: "Acompañamiento legal y de escrituración" },
      ],
    },
    quote: {
      text: "Cada propiedad es una decisión de largo plazo. Mi trabajo es que esa decisión esté bien tomada.",
      author: "— Dahiana Marín",
    },
    stats: {
      title: "En números",
      portfolio: "Propiedades en cartera",
      deals: "Operaciones acompañadas",
      years: "Años de experiencia",
      zones: "Barrios y zonas cubiertas",
    },
    services: {
      eyebrow: "Servicios",
      title: "¿Cómo puedo ayudarte?",
      items: [
        {
          n: "01",
          title: "¿Querés vender o alquilar tu propiedad?",
          text: "Te acompaño en cada paso: tasación, fotos profesionales, publicación destacada y negociación hasta la firma.",
          cta: "Solicitar tasación",
          to: "/vende-tu-propiedad",
        },
        {
          n: "02",
          title: "¿Buscás tu próxima inversión?",
          text: "Selección de oportunidades con potencial de plusvalía en Asunción y Central, con análisis de rentabilidad real.",
          cta: "Ver oportunidades",
          to: "/venta/departamentos",
        },
        {
          n: "03",
          title: "¿Necesitás alquilar?",
          text: "Búsqueda personalizada, filtrado de inquilinos y gestión contractual completa para propietarios e inquilinos.",
          cta: "Ver alquileres",
          to: "/alquiler/departamentos",
        },
      ],
    },
    invest: {
      eyebrow: "Inversiones",
      title: "Invertir en Paraguay.",
      subtitle:
        "Uno de los mercados más atractivos de Sudamérica — estabilidad, rentabilidad y crecimiento sostenido.",
      cta: "Leer la guía completa",
      items: [
        {
          title: "Alta rentabilidad y plusvalía",
          text: "El mercado paraguayo ofrece un potencial significativo de apreciación de capital por su crecimiento constante y demanda creciente.",
        },
        {
          title: "Incentivos fiscales atractivos",
          text: "Régimen fiscal favorable para inversores, con tasas impositivas bajas que maximizan la rentabilidad de tu inversión.",
        },
        {
          title: "Abierto a extranjeros",
          text: "Los extranjeros pueden comprar propiedades en las mismas condiciones que los paraguayos, sin restricciones de titularidad.",
        },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      title: "Preguntas frecuentes",
      subtitle: "Lo que más nos consultan antes de comprar, vender o alquilar en Paraguay.",
      items: [
        {
          q: "¿Cómo doy mi propiedad para vender o alquilar?",
          a: "Escribime por WhatsApp o completá el formulario de 'Vendé tu propiedad'. Coordinamos una visita, hago la tasación sin costo y definimos juntos el precio y la estrategia de publicación.",
        },
        {
          q: "Quiero comprar o alquilar, ¿cómo me pueden ayudar?",
          a: "Definimos tu presupuesto, zona y prioridades, y te envío una selección filtrada de la cartera. Coordino las visitas y te acompaño en la negociación y en toda la documentación.",
        },
        {
          q: "¿Un extranjero puede comprar una propiedad en Paraguay?",
          a: "Sí. Los extranjeros pueden adquirir inmuebles en las mismas condiciones que un ciudadano paraguayo. Solo se necesita cédula de identidad paraguaya o pasaporte y RUC según el caso.",
        },
        {
          q: "¿Qué gastos tiene la escrituración?",
          a: "En general se estima entre 3% y 5% del valor de la operación entre honorarios de escribanía, impuestos y sellados de transferencia. Te paso el detalle exacto antes de firmar.",
        },
        {
          q: "¿Trabajás con créditos hipotecarios?",
          a: "Sí. Te orientamos sobre los requisitos de los bancos y del BNF, y preparamos la documentación de la propiedad para que el proceso de aprobación sea lo más ágil posible.",
        },
      ],
    },
    timeline: {
      eyebrow: "Trayectoria",
      title: "Un recorrido construido en gestión, no en casualidad.",
      items: [
        {
          n: "01",
          title: "Origen",
          text: "Formación comercial y años de trabajo en atención y negociación con clientes, la base de una asesoría enfocada en las personas antes que en la transacción.",
        },
        {
          n: "02",
          title: "Inicio en bienes raíces",
          text: "Primeras operaciones en Asunción y Central, construyendo una cartera propia con foco en propiedades residenciales de calidad.",
        },
        {
          n: "03",
          title: "Especialización",
          subtitle: "Proyectos y desarrollos",
          text: "Incorporación de unidades en pozo y proyectos en desarrollo, sumando asesoría en planes de pago e inversión.",
        },
        {
          n: "04",
          title: "Hoy",
          subtitle: "Cartera propia y clientes internacionales",
          text: "Portal propio con cartera curada, atención en español, inglés y portugués, y acompañamiento integral desde la búsqueda hasta la escritura.",
        },
      ],
    },
    fab: "Escribinos por WhatsApp",
  },

  en: {
    brand: {
      eyebrow: "Real estate advisor · Asunción & Central, Paraguay",
      name: "Dahiana Marín",
      tagline: "Strategic real estate advice for people who think in years.",
      ctaProperties: "View properties",
      ctaMeeting: "Book a meeting",
      credentials: [
        { icon: "trophy", label: "Curated portfolio of premium properties" },
        { icon: "medal", label: "Personal service from start to finish" },
        { icon: "handshake", label: "Legal and closing support included" },
      ],
    },
    quote: {
      text: "Every property is a long-term decision. My job is to make sure that decision is the right one.",
      author: "— Dahiana Marín",
    },
    stats: {
      title: "By the numbers",
      portfolio: "Properties in portfolio",
      deals: "Deals supported",
      years: "Years of experience",
      zones: "Neighborhoods covered",
    },
    services: {
      eyebrow: "Services",
      title: "How can I help you?",
      items: [
        {
          n: "01",
          title: "Want to sell or rent out your property?",
          text: "I guide you through every step: valuation, professional photography, featured listing and negotiation up to signing.",
          cta: "Request a valuation",
          to: "/vende-tu-propiedad",
        },
        {
          n: "02",
          title: "Looking for your next investment?",
          text: "A selection of opportunities with real appreciation potential in Asunción and Central, with honest yield analysis.",
          cta: "See opportunities",
          to: "/venta/departamentos",
        },
        {
          n: "03",
          title: "Need to rent?",
          text: "Tailored search, tenant screening and full contract management for both landlords and tenants.",
          cta: "See rentals",
          to: "/alquiler/departamentos",
        },
      ],
    },
    invest: {
      eyebrow: "Investments",
      title: "Investing in Paraguay.",
      subtitle:
        "One of South America's most attractive markets — stability, yield and sustained growth.",
      cta: "Read the full guide",
      items: [
        {
          title: "High yield and appreciation",
          text: "The Paraguayan market offers significant capital appreciation potential thanks to steady growth and rising demand.",
        },
        {
          title: "Attractive tax incentives",
          text: "A favorable tax regime for investors, with low tax rates that maximize the return on your investment.",
        },
        {
          title: "Open to foreign buyers",
          text: "Foreigners can buy property under the same conditions as Paraguayan citizens, with no ownership restrictions.",
        },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      title: "Frequently asked questions",
      subtitle: "The questions we hear most before buying, selling or renting in Paraguay.",
      items: [
        {
          q: "How do I list my property for sale or rent?",
          a: "Message me on WhatsApp or fill in the 'Sell your property' form. We schedule a visit, I provide a free valuation and together we set the price and listing strategy.",
        },
        {
          q: "I want to buy or rent — how can you help?",
          a: "We define your budget, area and priorities, and I send you a filtered selection from the portfolio. I arrange viewings and support you through negotiation and paperwork.",
        },
        {
          q: "Can a foreigner buy property in Paraguay?",
          a: "Yes. Foreigners can purchase real estate under the same conditions as Paraguayan citizens. You only need a Paraguayan ID or passport, and a tax ID depending on the case.",
        },
        {
          q: "What are the closing costs?",
          a: "Typically 3% to 5% of the transaction value, covering notary fees, taxes and transfer stamps. You get the exact breakdown before signing.",
        },
        {
          q: "Do you work with mortgages?",
          a: "Yes. We walk you through bank requirements and prepare the property documentation so the approval process moves as fast as possible.",
        },
      ],
    },
    timeline: {
      eyebrow: "Track record",
      title: "A path built on management, not on chance.",
      items: [
        {
          n: "01",
          title: "Origin",
          text: "A commercial background and years of client-facing negotiation — the foundation of advice focused on people before transactions.",
        },
        {
          n: "02",
          title: "Entering real estate",
          text: "First deals in Asunción and Central, building an own portfolio focused on quality residential property.",
        },
        {
          n: "03",
          title: "Specialization",
          subtitle: "Projects and developments",
          text: "Adding off-plan units and developments, with advice on payment plans and investment returns.",
        },
        {
          n: "04",
          title: "Today",
          subtitle: "Own portfolio and international clients",
          text: "A dedicated portal with a curated portfolio, service in Spanish, English and Portuguese, and end-to-end support from search to deed.",
        },
      ],
    },
    fab: "Chat on WhatsApp",
  },

  pt: {
    brand: {
      eyebrow: "Consultora imobiliária · Assunção e Central, Paraguai",
      name: "Dahiana Marín",
      tagline: "Assessoria imobiliária estratégica para quem pensa em anos.",
      ctaProperties: "Ver imóveis",
      ctaMeeting: "Agendar reunião",
      credentials: [
        { icon: "trophy", label: "Portfólio selecionado de imóveis premium" },
        { icon: "medal", label: "Atendimento personalizado do início ao fim" },
        { icon: "handshake", label: "Acompanhamento jurídico e de escritura" },
      ],
    },
    quote: {
      text: "Cada imóvel é uma decisão de longo prazo. Meu trabalho é garantir que essa decisão seja bem tomada.",
      author: "— Dahiana Marín",
    },
    stats: {
      title: "Em números",
      portfolio: "Imóveis no portfólio",
      deals: "Operações acompanhadas",
      years: "Anos de experiência",
      zones: "Bairros e regiões atendidas",
    },
    services: {
      eyebrow: "Serviços",
      title: "Como posso ajudar você?",
      items: [
        {
          n: "01",
          title: "Quer vender ou alugar seu imóvel?",
          text: "Acompanho cada etapa: avaliação, fotos profissionais, anúncio em destaque e negociação até a assinatura.",
          cta: "Solicitar avaliação",
          to: "/vende-tu-propiedad",
        },
        {
          n: "02",
          title: "Procura seu próximo investimento?",
          text: "Seleção de oportunidades com potencial de valorização em Assunção e Central, com análise de rentabilidade real.",
          cta: "Ver oportunidades",
          to: "/venta/departamentos",
        },
        {
          n: "03",
          title: "Precisa alugar?",
          text: "Busca personalizada, seleção de inquilinos e gestão contratual completa para proprietários e inquilinos.",
          cta: "Ver aluguéis",
          to: "/alquiler/departamentos",
        },
      ],
    },
    invest: {
      eyebrow: "Investimentos",
      title: "Investir no Paraguai.",
      subtitle:
        "Um dos mercados mais atrativos da América do Sul — estabilidade, rentabilidade e crescimento constante.",
      cta: "Ler o guia completo",
      items: [
        {
          title: "Alta rentabilidade e valorização",
          text: "O mercado paraguaio oferece grande potencial de valorização do capital pelo crescimento constante e pela demanda crescente.",
        },
        {
          title: "Incentivos fiscais atrativos",
          text: "Regime fiscal favorável ao investidor, com alíquotas baixas que maximizam o retorno do seu investimento.",
        },
        {
          title: "Aberto a estrangeiros",
          text: "Estrangeiros podem comprar imóveis nas mesmas condições que os paraguaios, sem restrições de titularidade.",
        },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      title: "Perguntas frequentes",
      subtitle: "O que mais perguntam antes de comprar, vender ou alugar no Paraguai.",
      items: [
        {
          q: "Como coloco meu imóvel à venda ou para alugar?",
          a: "Me escreva pelo WhatsApp ou preencha o formulário 'Venda seu imóvel'. Agendamos uma visita, faço a avaliação sem custo e definimos juntos o preço e a estratégia de anúncio.",
        },
        {
          q: "Quero comprar ou alugar, como podem me ajudar?",
          a: "Definimos orçamento, região e prioridades, e envio uma seleção filtrada do portfólio. Agendo as visitas e acompanho a negociação e toda a documentação.",
        },
        {
          q: "Um estrangeiro pode comprar imóvel no Paraguai?",
          a: "Sim. Estrangeiros podem adquirir imóveis nas mesmas condições que um cidadão paraguaio. Basta documento de identidade paraguaio ou passaporte, e RUC conforme o caso.",
        },
        {
          q: "Quais são os custos de escritura?",
          a: "Em geral entre 3% e 5% do valor da operação, incluindo honorários de cartório, impostos e taxas de transferência. Você recebe o detalhe exato antes de assinar.",
        },
        {
          q: "Trabalham com financiamento imobiliário?",
          a: "Sim. Orientamos sobre os requisitos dos bancos e preparamos a documentação do imóvel para que a aprovação seja o mais ágil possível.",
        },
      ],
    },
    timeline: {
      eyebrow: "Trajetória",
      title: "Um caminho construído com gestão, não por acaso.",
      items: [
        {
          n: "01",
          title: "Origem",
          text: "Formação comercial e anos de negociação com clientes — a base de uma assessoria focada nas pessoas antes da transação.",
        },
        {
          n: "02",
          title: "Início no mercado imobiliário",
          text: "Primeiras operações em Assunção e Central, construindo um portfólio próprio com foco em imóveis residenciais de qualidade.",
        },
        {
          n: "03",
          title: "Especialização",
          subtitle: "Projetos e empreendimentos",
          text: "Incorporação de unidades na planta e empreendimentos, somando assessoria em planos de pagamento e investimento.",
        },
        {
          n: "04",
          title: "Hoje",
          subtitle: "Portfólio próprio e clientes internacionais",
          text: "Portal próprio com portfólio selecionado, atendimento em espanhol, inglês e português, e acompanhamento integral da busca até a escritura.",
        },
      ],
    },
    fab: "Fale pelo WhatsApp",
  },
};

export function faqJsonLd(lang: SeoLang) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: SITE_COPY[lang].faq.items.map((i) => ({
      "@type": "Question",
      name: i.q,
      acceptedAnswer: { "@type": "Answer", text: i.a },
    })),
  };
}
